import { isSignal, DestroyRef, assertInInjectionContext, inject, Injector, isDevMode, computed, effect, untracked, signal, RendererFactory2, ElementRef, PLATFORM_ID } from '@angular/core';
import { SIGNAL, signalSetFn, createSignal, signalUpdateFn } from '@angular/core/primitives/signals';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';

// export function createInterpolator(currentValue: number): InterpolateFactoryFn<number>;
// export function createInterpolator(currentValue: number[]): InterpolateFactoryFn<number[]>;
// export function createInterpolator(currentValue: Record<string | number | symbol, number>): InterpolateFactoryFn<Record<string | number | symbol, number>>;
/** Creates an interpolator using an initial {@link NumericValues} to determine the type of function to use when interpolating future values.. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createInterpolator(currentValue) {
  if (typeof currentValue === 'number') {
    return (a, b) => progress => interpolateNumber(a, b, progress);
  } else if (Array.isArray(currentValue)) {
    return (a, b) => progress => b.map((val, index) => interpolateNumber(a[index] ?? val, val, progress));
  }
  return (a, b) => progress => Object.entries(b).reduce((acc, cur) => {
    acc[cur[0]] = interpolateNumber(a[cur[0]] ?? cur[1], cur[1], progress);
    return acc;
  }, {});
  function interpolateNumber(a, b, progress) {
    return a * (1 - progress) + b * progress;
  }
}

/**
 * Tests if an object is valid for coerceSignal and meets the criteria for being a {@link ReactiveSource}.
 *
 * @param obj Any type of a value can be checked.
 */
function isReactive(obj) {
  return obj != null && (isSignal(obj) || isReactiveSourceFunction(obj) || isToSignalInput(obj));
}
/** Is true if obj is a function, it has no arguments, and it is not a signal. */
function isReactiveSourceFunction(obj) {
  return typeof obj === 'function' && obj.length === 0 && !isSignal(obj);
}
/**
 * Determines if an object is a parameter for toSignal by looking for subscribe property.
 * It does this by seeing if there is a subscribe function.
 * If toSignal's implementation changes, then this needs to be reviewed.
 *
 * @param obj Any type of a value can be checked.
 */
function isToSignalInput(obj) {
  return typeof obj?.subscribe === 'function';
}

/**
 * This is inspired by `signalAsReadonlyFn` from https://github.com/angular/angular/blob/main/packages/core/src/render3/reactivity/signal.ts#L90
 * It does not cache the readonlyFn, just creates a new one each time.
 */
function asReadonlyFnFactory($src) {
  const $readonly = () => $src();
  $readonly[SIGNAL] = $src[SIGNAL];
  return () => $readonly;
}
/** Gets the DestroyRef either using the passed injector or inject function. */
function getDestroyRef(fnType, injector) {
  if (injector) {
    return injector.get(DestroyRef);
  }
  assertInInjectionContext(fnType);
  return inject(DestroyRef);
}
/** Gets the injector, throwing if the function is in injection context.  */
function getInjector(fnType) {
  assertInInjectionContext(fnType);
  return inject(Injector);
}
/**
 * A type safe way for determining a key is in an object.
 * This is still needed because when "in" is used the propertyType can be inferred as undefined.
 */
function hasKey(obj, key) {
  return obj != null && key in obj;
}
// an interesting idea - the type passed is just an object's methods
// export function isMethodKey<T extends { [K in keyof T]: T[K] extends (...args: any[]) => any ? T[K] : never }>(obj: T, key: unknown): key is keyof T {
//   return (typeof obj[key as keyof T] === 'function');
// }
/** Detects if a key is a key of an object's method */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isMethodKey(obj, key) {
  return obj != null && typeof obj[key] === 'function';
}
/** Sets the debugName on a {@link ReactiveNode} from debugName property if it is set and if {@link isDevMode} returns true. */
function setDebugNameOnNode(node, debugName) {
  if (isDevMode() && debugName != null) {
    node.debugName = debugName;
  }
}
/** Sets the equal on a {@link SignalNode} if equalFn is defined. */
function setEqualOnNode(node, equalFn) {
  if (equalFn != null) {
    node.equal = equalFn;
  }
}

/**
 * Converts a source to a signal.
 * * If it is already a signal then it is returned.
 * * If it matches the input type to toSignal function, then it is converted to a signal.
 *   If initial value is passed then it doesn't have to be an async observable.
 * * If it is just a function then it is converted to a signal with computed.
 *
 * @example
 * ```ts
 * const signalInput = signal(1);
 * const functionInput = () => signalInput() * 2;
 * const immediateInput = timer(0, 1000);
 * const delayedInput = timer(1000, 1000);
 *
 * const coercedSignal = coerceSignal(signalInput);
 * const coercedFunction = coerceSignal(functionInput);
 * const coercedImmediate = coerceSignal(immediateInput);
 * const coercedDelayed = coerceSignal(delayedInput, { initialValue: 0 });
 *
 * effect(() => {
 *   console.log(coercedSignal, coercedFunction, coercedImmediate, coercedDelayed);
 * });
 * ```
 */
function coerceSignal(source, options) {
  if (isSignal(source)) {
    return source;
  } else if (isToSignalInput(source)) {
    // if there is no initialValue then assume the observable has an initial value and set requireSync as true.
    // Options are explicitly passed to avoid unintended values from effecting output.
    return hasKey(options, 'initialValue') ? toSignal(source, {
      injector: options.injector,
      initialValue: options.initialValue
    }) : toSignal(source, {
      injector: options?.injector,
      requireSync: true
    });
  }
  return computed(source);
}

/** Gets a function for requesting animation frames.  Either requestAnimationFrame or a setTimeout approximating 30 fps. */
function getRequestAnimationFrame() {
  return !globalThis.requestAnimationFrame ? callback => setTimeout(() => callback(Date.now()), 33) : globalThis.requestAnimationFrame;
}

/** Request animation frame function */
const requestAnimationFrame = getRequestAnimationFrame();
// export function animatedSignalFactory<V extends ValueSource<number>, O extends AnimationOptions, TState extends object>(
//   source: V, 
//   options: Partial<AnimatedNumericSignalOptions<number, O>> | undefined,
//   defaultAnimationOptions: Required<O>,
//   initialState: TState,
//   stepFn: AnimationStepFn<TState, O>
// ): V extends ReactiveSource<number> ? AnimatedSignal<number, O> : WritableAnimatedSignal<number, O>
// export function animatedSignalFactory<T extends NumericValues, V extends ValueSource<T>, O extends AnimationOptions, TState extends object>(
//   source: V, 
//   options: Partial<AnimatedNumericSignalOptions<T, O>> | undefined,
//   defaultAnimationOptions: Required<O>,
//   initialState: TState,
//   stepFn: AnimationStepFn<TState, O>
// ): V extends ReactiveSource<T> ? AnimatedSignal<T, O> : WritableAnimatedSignal<T, O>
// export function animatedSignalFactory<T, V extends ValueSource<T>, O extends AnimationOptions, TState extends object>(
//   source: V, 
//   options: Partial<AnimatedSignalOptions<T, O>> | undefined,
//   defaultAnimationOptions: Required<O>,
//   initialState: TState,
//   stepFn: AnimationStepFn<TState, O>
// ): V extends ReactiveSource<T> ? AnimatedSignal<T, O> : WritableAnimatedSignal<T, O>
function animatedSignalFactory(source, options, defaultAnimationOptions, initialState, stepFn) {
  const [/** The output signal that will be returned and have methods added it it if writable. */
  $output, /** A function that will get the current value of the source.  It could be a signal */
  signalValueFn] = isReactive(source) ? createFromReactiveSource(source, options) : createFromValue(source, options);
  /** THe SignalNode of the output signal.  Used when the output is set during value changes.  */
  const outputNode = $output[SIGNAL];
  // can't just use a spread since an option can be undefined.
  const defaults = {
    ...defaultAnimationOptions,
    interpolator: options?.interpolator || createInterpolator(outputNode.value)
  };
  if (options) {
    overwriteProperties(defaults, options);
  }
  let delayTimeoutId = undefined;
  let instanceId = 0;
  let state;
  effect(onCleanup => {
    const priorValue = untracked($output);
    const [nextValue, overrideOptions] = signalValueFn();
    if (nextValue === priorValue) {
      // since an array is being passed from signalValueFn, it could be the same value was sent.
      return;
    }
    const animationOptions = overrideOptions ? {
      ...overwriteProperties({
        ...defaults
      }, overrideOptions)
    } : defaults;
    const interpolate = (overrideOptions?.interpolator || defaults.interpolator)(priorValue, nextValue);
    const thisInstanceId = ++instanceId;
    // in case a previous animation was delayed then clear it before it starts.
    clearTimeout(delayTimeoutId);
    if (animationOptions.delay) {
      delayTimeoutId = setTimeout(start, animationOptions.delay);
    } else {
      start();
    }
    function start() {
      const timeCurrent = Date.now(); // performance.now can't be tested [SAD EMOJI] (switch to Jest?)
      state = {
        ...initialState,
        ...state,
        isDone: false,
        progress: 0,
        timeCurrent,
        timeElapsed: 0,
        timeDelta: 0,
        timeStart: timeCurrent
      };
      stepFn(state, animationOptions); // run initial step function in case animation isn't necessary.
      if (state.isDone) {
        // don't bother with the animation since its done already.
        signalSetFn(outputNode, nextValue);
        return;
      }
      signalSetFn(outputNode, interpolate(state.progress));
      requestAnimationFrame(step);
    }
    function step() {
      if (thisInstanceId !== instanceId) {
        return; // another effect has occurred.
      }
      // I'm not sure if this is necessary.  It might have been something I copied from an animation example.
      // if (previousTime === time) {
      //   requestAnimationFrame(step); // no time elapsed.
      //   return;
      // }
      const timeCurrent = Date.now(); // performance.now can't be tested [SAD EMOJI] (switch to Jest?)
      state.timeDelta = timeCurrent - state.timeCurrent;
      state.timeCurrent = timeCurrent;
      state.timeElapsed = timeCurrent - state.timeStart;
      stepFn(state, animationOptions);
      signalSetFn(outputNode, interpolate(state.progress));
      if (!state.isDone) {
        requestAnimationFrame(step);
      }
    }
    // force stop by incrementing instanceId on destroy.
    onCleanup(() => instanceId++);
  }, options);
  return $output;
  /** Coerces a source signal from signal input and creates the output signal.. */
  function createFromReactiveSource(reactiveSource, signalOptions) {
    const $source = coerceSignal(reactiveSource, signalOptions);
    const $output = signal(untracked($source), signalOptions);
    $output.setOptions = options => overwriteProperties(defaults, options);
    const signalValueFn = () => [$source()];
    return [$output, signalValueFn];
  }
  /** Creates a writable source signal and output signal from the initial value. */
  function createFromValue(sourceValue, signalOptions) {
    const $output = signal(sourceValue, signalOptions);
    const $source = createSignal([sourceValue, undefined]);
    const sourceNode = $source[SIGNAL];
    $output.set = (x, options) => signalSetFn(sourceNode, [x, options]);
    $output.setOptions = options => overwriteProperties(defaults, options);
    $output.update = (updateFn, options) => signalUpdateFn(sourceNode, ([value]) => [updateFn(value), options]);
    const signalValueFn = $source;
    return [$output, signalValueFn];
  }
}
/**
 * Sets values on {@link target} if they are defined in both {@link target} and {@link values}.
 * Different then spread operator as it will ignore undefined values.
 * @returns The value of {@link target}.
 */
function overwriteProperties(target, values) {
  Object.entries(values).forEach(([key, value]) => {
    if (key in target && value !== undefined) {
      target[key] = value;
    }
  });
  return target;
}
const DEFAULT_OPTIONS$1 = {
  clamp: false,
  damping: 3,
  delay: 0,
  precision: 0.01,
  stiffness: 100
};
/**
 * Creates a signal whose value morphs from the old value to the new over a specified duration.
 * @param source Either a value, signal, observable, or function that can be used in a computed function.
 * @param options Options for the signal.  If a number, number[] or Record<string | number symbol, number>
 * is passed then this is not required.  Otherwise an interpolator is required to translate the change of the value.
 * @example
 * ```ts
 * const $animatedValue = springSignal(1, { damping: 3, stiffness: 100 });
 * function demo(): void {
 *   $animatedValue.set(5);
 * }
 * ```
 */
function springSignal(source, options) {
  return animatedSignalFactory(source, options, DEFAULT_OPTIONS$1, {
    velocity: 1
  }, (state, options) => {
    const dt = state.timeDelta / 1000;
    const force = -options.stiffness * (state.progress - 1);
    const damping = options.damping * state.velocity;
    const acceleration = force - damping;
    state.velocity += acceleration * dt;
    state.progress += state.velocity * dt;
    if (Math.abs(1 - state.progress) < options.precision && Math.abs(state.velocity / dt) <= options.precision / dt) {
      state.isDone = true;
      state.progress = 1;
      return;
    }
    if (options.clamp && state.progress > 1) {
      state.progress = 2 - state.progress;
      state.velocity = -state.velocity + damping * dt;
    }
  });
}
const DEFAULT_OPTIONS = {
  delay: 0,
  duration: 400,
  easing: x => x
};
/**
 * Creates a signal whose value morphs from the old value to the new over a specified duration.
 * @param source Either a value, signal, observable, or function that can be used in a computed function.
 * @param options Options for the signal.  If a number, number[] or Record<string | number symbol, number>
 * is passed then this is not required.  Otherwise an interpolator is required to translate the change of the value.
 * @example
 * ```ts
 * const fastLinearChange = tweenSignal(1);
 * const slowEaseInChange = tweenSignal(1, { duration: 5000, easing: (x) => return  x ** 2; });
 * function demo(): void {
 *   fastLinearChange.set(5); // in 400ms will display something like 1, 1.453, 2.134, 3.521, 4.123, 5.
 *   slowEaseInChange.set(5, { duration: 10000 }); // in 10000ms will display something like 1, 1.21, 1.4301...
 * }
 * ```
 */
function tweenSignal(source, options) {
  return animatedSignalFactory(source, options, DEFAULT_OPTIONS, {}, (state, options) => {
    const timeProgress = options.duration > 0 ? Math.min(1, state.timeElapsed / options.duration) : 1;
    state.isDone = timeProgress === 1;
    state.progress = options.easing(timeProgress);
  });
}
const VOID_FN = () => {};
var AsyncSignalStatus = /*#__PURE__*/function (AsyncSignalStatus) {
  AsyncSignalStatus[AsyncSignalStatus["Error"] = 0] = "Error";
  AsyncSignalStatus[AsyncSignalStatus["Ok"] = 1] = "Ok";
  AsyncSignalStatus[AsyncSignalStatus["NotSet"] = 2] = "NotSet";
  return AsyncSignalStatus;
}(AsyncSignalStatus || {});
/**
 * Takes an async source (Promise, Observable) or signal/function that returns an async source
 * and returns that source's values as part of a signal.  Kind of like an rxjs flattening operator.
 * When the async source changes, the old source is immediately released and the new source is listened.
 * @param valueSource A Promise or Subscribable to create writable signal,
 * otherwise a signal or function that returns a Promise or Subscribable.
 * @param options The options for the async signal
 * @returns a signal that returns values from the async source..
 * @example
 * ```ts
 * $id = signal(0);
 * // call getCustomer every time $id changes.
 * $customer = asyncSignal(() => this.$id() !== 0 ? this.getCustomer(this.$id()) : undefined);
 *
 * constructor() {
 *   // writable overload can switch versions.
 *   const artificialWritableExampleSource1 = new BehaviorSubject(1);
 *   const $writable = asyncSignal(artificialWritableExampleSource1);
 *   const artificialWritableExampleSource2 = new BehaviorSubject(2);
 *   $writable.set(artificialWritableExampleSource2);
 * }
 * ```
 */
function asyncSignal(valueSource, options = {}) {
  return isSignal(valueSource) ? createFromSignal(valueSource, options) : isReactiveSourceFunction(valueSource) ? createFromReactiveSourceFunction(valueSource, options) : createFromValue$2(valueSource, options);
}
/** Called if this is a function that's NOT a signal to create a readonly AsyncSignal. */
function createFromReactiveSourceFunction(reactiveFn, options) {
  const $input = coerceSignal(reactiveFn, {
    initialValue: undefined,
    injector: options.injector
  });
  return createFromSignal($input, options);
}
/** Creates the writable version of an async signal from an initial async source. */
function createFromValue$2(initialSource, options) {
  const $input = createSignal(initialSource);
  const inputNode = $input[SIGNAL];
  const $output = createFromSignal($input, options);
  $output.asReadonly = asReadonlyFnFactory($output);
  $output.set = value => signalSetFn(inputNode, value);
  $output.update = updateFn => signalUpdateFn(inputNode, updateFn);
  return $output;
}
/** Creates a readonly version of an async signal from another signal returning an async source. */
function createFromSignal($input, options) {
  /**
   * The current source of asynchronous values.
   * Initially this is undefined because we're trying to defer reading the source until the effect first runs.
   * If requireSync is true then it will get the value immediately
   */
  let currentSource;
  /** An "unsubscribe" function. */
  let currentListenerCleanupFn;
  // if requireSync is true, the set the state as NotSet, otherwise it's Ok.  Being NotSet and read will throw an error.
  const $state = signal(options.requireSync ? {
    status: AsyncSignalStatus.NotSet,
    value: options.defaultValue
  } : {
    status: AsyncSignalStatus.Ok,
    value: options.defaultValue
  });
  // if requireSync is true then immediately start listening.
  if (options?.requireSync) {
    currentSource = untracked($input);
    currentListenerCleanupFn = updateListener(currentSource);
  } else {
    currentListenerCleanupFn = () => {};
  }
  effect(() => {
    // Initially this used to only run inside a conditional branch if the state was OK.
    // The problem with that was if another source had an error, the error would bubble up, since we're not catching it.
    const nextSource = $input();
    if (nextSource === currentSource) {
      // don't start listening to an already listened to source.
      // This is only necessary in case requireSync was true and this is the first effect was run.
      return;
    }
    // manually cleanup old listener.
    currentListenerCleanupFn();
    // store the currentSource so it can be used in next invocation of effect.
    currentSource = nextSource;
    // Call the updateListener process and set currentListenerCleanupFn from result.
    // (This is untracked because a signal may be used inside the source and cause additional invocations.)
    currentListenerCleanupFn = untracked(() => updateListener(nextSource));
  }, {
    injector: options.injector
  });
  // Call cleanup on the last listener.  The effect cleanup can't be used because of the risk of initially repeating subscriptions.
  getDestroyRef(asyncSignal, options.injector).onDestroy(() => currentListenerCleanupFn());
  return computed(() => {
    const {
      err,
      status,
      value
    } = $state();
    switch (status) {
      case AsyncSignalStatus.Error:
        throw new Error('Error in Async Source', {
          cause: err
        });
      case AsyncSignalStatus.NotSet:
        throw new Error('requireSync is true, but no value was returned from asynchronous source.');
      case AsyncSignalStatus.Ok:
        return value;
    }
  }, options // pass along the debugName and equal options.
  );
  /** Starts listening to the new async value, and returns the cleanup function. */
  function updateListener(asyncSource) {
    // This was removed because it was confusing that a value could be a subscribable and a subscribable could be converted to a signal.
    // if (asyncSource === undefined) {
    //   return VOID_FN; // don't listen to anything and return a dummy unsubscribe function.
    // }
    if ('subscribe' in asyncSource) {
      const unsubscribe = asyncSource.subscribe({
        error: setError,
        next: setValue
      });
      return () => unsubscribe.unsubscribe();
    }
    asyncSource.then(setValue, setError).catch(setError);
    return VOID_FN; // there is no way to cleanup a promise that I know of.
    /** Sets the state of errored if an error hadn't already occurred. */
    function setError(err) {
      $state.update(cur => cur.status === AsyncSignalStatus.Error ? cur : {
        err,
        status: AsyncSignalStatus.Error,
        value: cur.value
      });
    }
    /** Updates value if the status isn't error. */
    function setValue(value) {
      $state.update(cur => cur.status === AsyncSignalStatus.Error ? cur : {
        status: AsyncSignalStatus.Ok,
        value
      });
    }
  }
}

/** The status of the timer. */
var TimerStatus = /*#__PURE__*/function (TimerStatus) {
  TimerStatus[TimerStatus["Destroyed"] = 0] = "Destroyed";
  TimerStatus[TimerStatus["Paused"] = 1] = "Paused";
  TimerStatus[TimerStatus["Running"] = 2] = "Running";
  TimerStatus[TimerStatus["Stopped"] = 3] = "Stopped";
  return TimerStatus;
}(TimerStatus || {});
/** A general timer. */
class TimerInternal {
  /** Gets or set intervalTime if this was started with an interval.  Will throw if not initially passed an interval. */
  get intervalTime() {
    TimerInternal.assertHasIntervalRunner(this);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.intervalRunner.dueTime;
  }
  set intervalTime(value) {
    TimerInternal.assertHasIntervalRunner(this);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.updateRunnerDueTime(value, this.intervalRunner);
  }
  /** Gets the number of ticks since start. */
  get ticks() {
    return this.tickCount;
  }
  /** Gets or sets the timeoutTime. */
  get timeoutTime() {
    return this.timeoutRunner.dueTime;
  }
  set timeoutTime(value) {
    this.updateRunnerDueTime(value, this.timeoutRunner);
  }
  /** Readonly status of the timer. */
  get timerStatus() {
    return this.status;
  }
  /**
   * passing only timeoutTime will have this behave like a timeout.
   * passing intervalTime will have this first execute timeoutTime then intervalTime.
   */
  constructor(timeoutTime, intervalTime, options) {
    /** The time the last tick completed.  Doesn't have to be the actual last time. */
    this.lastCompleteTime = Date.now();
    /** When paused, stores what time was remaining. */
    this.remainingTimeAtPause = 0;
    /** The current status of timer.  Do not modify directly!  All changes should go through setStatus. */
    this.status = TimerStatus.Stopped;
    /** The count of ticks */
    this.tickCount = 0;
    this.runner = this.timeoutRunner = {
      dueTime: timeoutTime,
      onTickComplete: this.onTimeoutTickComplete.bind(this)
    };
    if (intervalTime !== undefined) {
      this.intervalRunner = {
        dueTime: intervalTime,
        onTickComplete: this.tickStart.bind(this) // loop
      };
    }
    this.onTickCallback = options?.onTick ?? (() => undefined);
    this.onStatusChangeCallback = options?.onStatusChange ?? (() => undefined);
    if (options?.runAtStart) {
      this.setStatus(TimerStatus.Running);
      this.tickStart();
    }
  }
  /** Clears the current tick and prevents any future processing. */
  destroy() {
    clearTimeout(this.timeoutId);
    this.setStatus(TimerStatus.Destroyed);
  }
  /** Pauses the timer. */
  pause() {
    if (this.status === TimerStatus.Running) {
      this.remainingTimeAtPause = this.getRemainingTime();
      clearTimeout(this.timeoutId);
      this.setStatus(TimerStatus.Paused);
    }
  }
  /** Resumes the timer if it was paused, otherwise does nothing. */
  resume() {
    if (this.status === TimerStatus.Paused) {
      this.setStatus(TimerStatus.Running);
      // if duration is adjusted by a signal then this is a problem.
      this.lastCompleteTime = Date.now() - (this.runner.dueTime - this.remainingTimeAtPause);
      this.tickStart();
    }
  }
  /** Start or restarts the timer as long as it isn't destroyed. */
  start() {
    if (this.status !== TimerStatus.Destroyed) {
      this.setStatus(TimerStatus.Running);
      this.tickCount = 0;
      this.runner = this.timeoutRunner;
      this.lastCompleteTime = Date.now();
      this.tickStart();
    }
  }
  /** Throws if intervalRunner isn't defined. */
  static assertHasIntervalRunner(ti) {
    if (!ti.intervalRunner) {
      throw new Error('This timer was not configured for intervals');
    }
    return true;
  }
  /** Determines the remaining time. */
  getRemainingTime() {
    return this.runner.dueTime - (Date.now() - this.lastCompleteTime);
  }
  /** Switch to intervalRunner or set as stopped. */
  onTimeoutTickComplete() {
    if (this.intervalRunner) {
      this.runner = this.intervalRunner;
      this.tickStart(); // begin intervalLoop
    } else {
      this.setStatus(TimerStatus.Stopped);
    }
  }
  setStatus(status) {
    if (this.status !== status) {
      this.status = status;
      this.onStatusChangeCallback(status);
    }
  }
  /**
   * Handles when a tick is complete.
   * If for some reason there is remaining time, it will restart the tick.
   * Otherwise it will increase the internalCount, execute the callback, update the completed,
   *   and call the current runner's onTickComplete method so that it handles the next step.
   */
  tickComplete() {
    const remainingTime = this.getRemainingTime();
    if (remainingTime > 0) {
      // this could occur if the end time changed.
      this.tickStart();
    } else {
      ++this.tickCount;
      this.onTickCallback(this.tickCount);
      this.lastCompleteTime = Date.now() + this.getRemainingTime();
      this.runner.onTickComplete();
    }
  }
  /** Attempts to starts the tick timeout. */
  tickStart() {
    clearTimeout(this.timeoutId);
    if (this.status === TimerStatus.Running) {
      this.timeoutId = setTimeout(this.tickComplete.bind(this), this.getRemainingTime());
    }
  }
  /** Sets the dueTime on the runner and if necessary starts the timer. */
  updateRunnerDueTime(dueTime, targetRunner) {
    const oldTime = targetRunner.dueTime;
    targetRunner.dueTime = dueTime;
    if (targetRunner === this.runner && this.status === TimerStatus.Running && oldTime > dueTime) {
      this.tickStart();
    }
  }
}

/**
 * Creates a function that retrieves the current value from a {@link ValueSource}.
 * If the value is an {@link ReactiveSource} like an observable or a function that returns a value, then it will create a signal,
 * otherwise it will return a function that just returns a value.
 */
function createGetValueFn(valueSrc, injector) {
  return isReactive(valueSrc) ? coerceSignal(valueSrc, {
    injector
  }) : () => valueSrc;
}
/**
 * Determines if valueSrcFn is a signal, and if it is, creates an effect from callbackFn.
 * Otherwise does nothing since the valueSrc is constant.
 * The factory is used just so the effect callback doesn't need any assertions on valueSrcFn.
 */
function watchValueSourceFn(valueSrcFn, callback, injector) {
  if (isSignal(valueSrcFn)) {
    effect(() => callback(valueSrcFn()), {
      injector: injector
    });
  }
}

/**
 * Creates either a writable signal whose values are debounced, or a signal who returns debounced values of another signal.
 */
function debounceSignal(initialValueOrSource, debounceTime, options) {
  return isReactive(initialValueOrSource) ? createFromReactiveSource(initialValueOrSource, debounceTime, options) : createFromValue$1(initialValueOrSource, debounceTime, options);
}
/** Creates a signal that debounces the srcSignal the given debounceTime. */
function createFromReactiveSource(sourceInput, debounceTime, options) {
  // QUESTION: Why are we explicitly ignore the options.equal function for the reactive source version?
  // We are even omitting it from the overload of the main function.
  // Is it because the equal function is running on the source signal in the value version?
  const timerTimeFn = createGetValueFn(debounceTime, options?.injector);
  const $source = coerceSignal(sourceInput, options);
  const $output = signal(untracked($source), {
    debugName: options?.debugName
  });
  ;
  const outputNode = $output[SIGNAL];
  const timer = new TimerInternal(timerTimeFn(), undefined, {
    onTick: () => signalSetFn(outputNode, untracked($source))
  });
  // setup cleanup actions.
  getDestroyRef(createFromReactiveSource, options?.injector).onDestroy(() => timer.destroy());
  watchValueSourceFn(timerTimeFn, x => timer.timeoutTime = x, options?.injector);
  effect(() => {
    $source(); // wish there was a better way to watch the value.
    timer.start();
  }, options);
  return $output;
}
/** Creates a writeable signal that updates after a certain amount of time. */
function createFromValue$1(initialValue, debounceTime, options) {
  const $source = createSignal(initialValue);
  const sourceNode = $source[SIGNAL];
  setEqualOnNode(sourceNode, options?.equal);
  const $debounced = createFromReactiveSource($source, debounceTime, options);
  $debounced.asReadonly = asReadonlyFnFactory($debounced);
  $debounced.set = value => signalSetFn(sourceNode, value);
  $debounced.update = updateFn => signalUpdateFn(sourceNode, updateFn);
  return $debounced;
}

/**
 *
 * @param observerFactoryFn Returns an observer that executes a callback whenever a notification is received.
 * @param source The Observed value.
 * @param options A mixture of options for the signal and the observer.
 * @param nativeObservedTransformFn Converts the source value into the appropriate type for the observer.
 * @param initialOutput The initial output for the signal.
 * @param injector The injector used to create an effect to monitor changes to the source if it is a signal.
 * @returns A signal whose value changes to the latest observer output.
 */
function domObserverSignalFactory(observerFactoryFn, source, options, nativeObservedTransformFn, debugName, injector) {
  const $observerOutput = createSignal([]);
  const observerOutputNode = $observerOutput[SIGNAL];
  setDebugNameOnNode(observerOutputNode, debugName);
  //const outputSetter = $observerOutput.set; // copy setter in case it is overwritten by writable output.
  const observer = observerFactoryFn(x => signalSetFn(observerOutputNode, x));
  const destroyRef = getDestroyRef(domObserverSignalFactory, injector);
  destroyRef.onDestroy(() => observer.disconnect());
  if (isReactive(source)) {
    domObserverComputedSignalSetup(observer, source, options, nativeObservedTransformFn, injector);
    return $observerOutput;
  } else {
    return domObserverWritableSignalFactory(observer, $observerOutput, source, options, nativeObservedTransformFn);
  }
}
/**
 * Creates a writable signal.
 * @param observer The observer that watches subjects.
 * @param $output The signal function to add methods to.  This will be mutated directly.
 * @param initialSubject The initially watched subject.
 * @param options A mixture of options for the signal and the observer.
*/
function domObserverWritableSignalFactory(observer, $output, initialSubject, options, nativeObservedTransformFn) {
  let untransformedSubject = initialSubject;
  observeNextSubject(observer, nativeObservedTransformFn(initialSubject), options);
  $output.asReadonly = asReadonlyFnFactory($output);
  $output.set = updateState;
  $output.update = updateFn => updateState(updateFn(untransformedSubject));
  return $output;
  function updateState(nextSubject, nextOptions) {
    if (nextSubject !== untransformedSubject || nextOptions !== undefined) {
      untransformedSubject = nextSubject;
      options = nextOptions ?? options;
      observeNextSubject(observer, nativeObservedTransformFn(nextSubject), options);
    }
  }
}
/** Sets up watching changes to the source signal and routes it to the observer. */
function domObserverComputedSignalSetup(observer, subjectSource, options, nativeObservedTransformFn, injector) {
  const $subject = coerceSignal(subjectSource, {
    injector
  });
  const subjectNode = $subject[SIGNAL];
  let currentSubject;
  if (!subjectNode.producerMustRecompute(subjectNode)) {
    // only start observing immediately IF the signal value is updated.  Otherwise wait for the effect.
    currentSubject = untracked($subject);
    observeNextSubject(observer, nativeObservedTransformFn(currentSubject), options);
  }
  effect(() => {
    const nextSubject = $subject();
    if (nextSubject !== currentSubject) {
      // only update the observer if the subject has Changed.
      currentSubject = nextSubject;
      observeNextSubject(observer, nativeObservedTransformFn(nextSubject), options);
    }
  }, {
    injector
  });
}
/** Common behavior when the subject changes. */
function observeNextSubject(observer, subject, options) {
  observer.disconnect();
  if (subject !== undefined) {
    // The types are a intersection of all possible targets which is not possible.
    // Also IntersectionObserver doesn't support options changing.
    observer.observe(subject, options);
  }
}

/**
 * Uses IntersectionObserver to observe changes to nodes passed to the signal.
 * @param source Either a signal/observable/function that returns Elements or ElementRefs, or a value that is Elements or ElementRef.
 * If the source is a value then the signal will be writable.
 * @param options Options for the signal or the IntersectionObserver used to monitor changes.
 * @example
 * ```ts
 * const el = document.getElementById('el1');
 * const $obs = intersectionSignal(el);
 * effect(() => console.log($obs()[0]?.attributeName)); // will log when scrolled into view.
 * el.scrollIntoView();
 * ```
 */
function intersectionSignal(source, options) {
  if (typeof IntersectionObserver === 'undefined') {
    return signal([], options); // return a dummy signal that never changes if there is no IntersectionObserver (like in SSR).
  }
  const injector = options?.injector ?? getInjector(intersectionSignal);
  return domObserverSignalFactory(callback => {
    const nativeOptions = options ? {
      ...options,
      root: getRoot(options.root)
    } : undefined;
    return new IntersectionObserver(callback, nativeOptions);
  }, source, undefined,
  // intersection observer options never change
  getElement$1, options?.debugName, injector);
}
/** Converts IntersectionSignalValue to Element.  If it cannot then undefined is returned. */
function getRoot(value) {
  if (value instanceof Element || value instanceof Document) {
    return value;
  }
  if (value != null && 'nativeElement' in value && value.nativeElement instanceof Element) {
    return value.nativeElement;
  }
  return undefined;
}
/** Converts IntersectionSignalValue to Element.  If it cannot then undefined is returned. */
function getElement$1(value) {
  if (value instanceof Element) {
    return value;
  }
  if (value?.nativeElement instanceof Element) {
    return value.nativeElement;
  }
  return undefined;
}

/**
 * Uses MutationObserver to observe changes to nodes passed to the signal.
 * @param source Either a signal/observable/function that returns Nodes or ElementRefs, or a value that is Node or ElementRef.
 * If the source is a value then the signal will be writable.
 * @param options Options for the signal or the MutationObserver used to monitor changes.
 * @example
 * ```ts
 * const el = document.getElementById('el1');
 * const $obs = mutationSignal(el);
 * effect(() => console.log($obs()[0]?.attributeName)); // will log 'data-node-value'
 * el.setAttribute('data-node-value', 'hello there');
 * ```
 */
function mutationSignal(source, options) {
  if (typeof MutationObserver === 'undefined') {
    return signal([], options); // return a dummy signal that never changes if there is no MutationObserver (like in SSR).
  }
  const injector = options?.injector ?? getInjector(mutationSignal);
  return domObserverSignalFactory(callback => new MutationObserver(callback), source, getObserverOptions(options), getNode, options?.debugName, injector);
}
/**
 * Extracts Observer options from Signal Options.
 * If no options are passed then a default of attributes is used.
 */
function getObserverOptions(options) {
  // careful here.
  // The logic is to return default mutationObserver options if the only options are a non-mutation one.
  if (options === undefined || Object.keys(options).every(x => x === 'injector')) {
    return {
      attributes: true
    };
  }
  return options; // it's probably safe to return options directly.
}
/** Converts MutationSignalValue to Node.  If it cannot then undefined is returned. */
function getNode(value) {
  if (value instanceof Node) {
    return value;
  }
  if (value?.nativeElement instanceof Node) {
    return value.nativeElement;
  }
  return undefined;
}

/**
 * Uses ResizeObserver to observe changes to elements passed to the signal.
 * @param source Either a signal/observable/function that returns Elements or ElementRefs, or a value that is Element or ElementRef.
 * If the source is a value then the signal will be writable.
 * @param options Options for the signal or the ResizeObserver used to monitor changes.
 * @example
 * ```ts
 * const el = document.getElementById('el1');
 * const $obs = resizeSignal(el);
 * effect(() => console.log($obs()[0])); // will output changes to size.
 * el.style.height = '250px';
 * ```
 */
function resizeSignal(source, options) {
  if (typeof ResizeObserver === 'undefined') {
    return signal([]); // return a dummy signal that never changes if there is no MutationObserver (like in SSR).
  }
  const injector = options?.injector ?? getInjector(resizeSignal);
  return domObserverSignalFactory(callback => new ResizeObserver(callback), source, options, getElement, options?.debugName, injector);
}
/** Converts MutationSignalValue to Node.  If it cannot then undefined is returned. */
function getElement(value) {
  if (value instanceof Element) {
    return value;
  }
  if (value?.nativeElement instanceof Element) {
    return value.nativeElement;
  }
  return undefined;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const DUMMY_FN = () => {};
function eventSignal(source, eventName, selectorOrOptions, optionsWhenSelectorPresent) {
  const [selector, options] = resolveSelectorAndOptions();
  const injector = options?.injector ?? getInjector(eventSignal);
  const $output = signal(options?.initialValue, options);
  const destroyRef = injector.get(DestroyRef);
  const renderer = injector.get(RendererFactory2).createRenderer(null, null);
  let destroyEventListener = DUMMY_FN;
  if (isReactive(source)) {
    listenToSignal(coerceSignal(source, options));
  } else {
    listenToValue(source);
  }
  destroyRef.onDestroy(() => {
    renderer.destroy();
    destroyEventListener();
  });
  return $output;
  function resolveSelectorAndOptions() {
    if (typeof selectorOrOptions === 'function') {
      return [selectorOrOptions, optionsWhenSelectorPresent];
    } else {
      return [evt => evt, selectorOrOptions];
    }
  }
  function listenToValue(target) {
    const targetListenable = coerceListenable(target);
    if (!targetListenable) {
      // console.warn('An undefined object was passed to eventSignal. It will not be listened to.');
      destroyEventListener = DUMMY_FN;
      return;
    }
    destroyEventListener = renderer.listen(targetListenable, eventName, event => {
      const nextValue = untracked(() => selector(event));
      $output.set(nextValue);
    });
  }
  function listenToSignal($target) {
    effect(onCleanup => {
      destroyEventListener();
      listenToValue($target());
      onCleanup(() => {
        destroyEventListener();
        // make sure event unlistener is a dummy function incase there are any ramifications for double calling.
        destroyEventListener = DUMMY_FN;
      });
    }, {
      injector
    });
  }
}
/** If target is elementRef then returns nativeElement, otherwise returns target */
function coerceListenable(target) {
  if (target instanceof ElementRef) {
    return target.nativeElement;
  }
  return target;
}
// THIS SEEMED UNNECESSARY:  Never encountered a scenario where Renderer2 could be injected.
// /** The theory here is that if inside component Renderer can be injected without the need of RendererFactory. */
// function injectRenderer(injector: Injector): [render: Renderer2, destroy: () => void] {
//   // if renderer exists in context already then there is no reason to create it again.
//   // so we can return it with a dummy destroyer.
//   let renderer = injector.get(Renderer2, null, { optional: true });
//   if (renderer) {
//     return [renderer, () => {}];
//   }
//   const rendererFactory = injector.get(RendererFactory2);
//   renderer = rendererFactory.createRenderer(null, null);
//   const destroy = () => renderer.destroy();
//   return [renderer, destroy];
// }

;
/**
 * Filters values set to a directly so that the value only changes when the filter is passed.
 * Some overloads allow for a guard function which will change the type of the signal's output value.
 * @example
 * ```ts
 * const nonNegative = filterSignal<number>(0, x => x >= 0);
 * nonNegative.set(-1);
 * console.log(nonNegative()); // [LOG]: 0
 * ```
 * @typeParam T The input value of the signal
 * @typeParam O The output type of the signal or the value of the input and output if no guard function is used.
 * @param initialValue The initial value of the signal
 * @param filterFn A function that filters values.  Can be a guard function.
 * @param options Options for the signal.
 * @returns A writable signal whose values are only updated when set.
 */
function filterSignal(initialValue, filterFn, options) {
  const $output = createSignal(initialValue);
  const outputNode = $output[SIGNAL];
  setDebugNameOnNode(outputNode, options?.debugName);
  setEqualOnNode(outputNode, options?.equal);
  $output.asReadonly = asReadonlyFnFactory($output);
  $output.set = setConditionally;
  $output.update = signalUpdateFn => setConditionally(signalUpdateFn(untracked($output)));
  return $output;
  /** Sets the signal value only if it passes the filter function. */
  function setConditionally(value) {
    if (filterFn(value)) {
      signalSetFn(outputNode, value);
    }
  }
}

/**
 * Lifts methods from the signal's value to the signal itself.
 * @example
 * ```ts
 * const awesomeArray = liftSignal([1, 2, 3, 4], ['filter'], ['push', 'pop']);
 * awesomeArray.push(5);
 * console.log(awesomeArray()); //[1, 2, 3, 4, 5];
 * awesomeArray.pop();
 * console.log(awesomeArray()); //[1, 2, 3, 4];
 * awesomeArray.filter(x => x % 2 === 0);
 * console.log(awesomeArray()); //[2, 4];
 * ```
 * @param valueSource Either a value or a Writable signal.
 * @param updaters A tuple that contains the names that will return a new value.
 * @param mutators A tuple that contains the names that will modify the signal's value directly.
 * To guarantee this will return a new value, structuredClone or object.assign is used to create a brand new object,
 * so use with caution.
 * @typeParam T the type of the signal's value as well as the type where the functions are lifted from.
 * @typeParam U A tuple that contains the names of methods appropriate for updating.
 * @typeParam M A tuple that contains the names of methods appropriate for mutating.
 */
function liftSignal(valueSource, updaters, mutators, options) {
  const $output = isSignal(valueSource) ? valueSource : signal(valueSource, options);
  const boundMethods = {};
  updaters?.forEach(cur => {
    boundMethods[cur] = (...args) => $output.update(x => x[cur](...args));
  });
  if (mutators) {
    const cloneFn = options?.cloneFn ?? cloneFnFactory($output());
    mutators.forEach(cur => {
      boundMethods[cur] = (...args) => $output.update(x => {
        const cloned = cloneFn(x);
        cloned[cur](...args);
        return cloned;
      });
    });
  }
  return Object.assign($output, boundMethods);
}
/** Creates a cloning function based on the sample object. */
function cloneFnFactory(sample) {
  return Array.isArray(sample) ? x => structuredClone(x) : x => Object.assign(Object.create(Object.getPrototypeOf(x)), x);
}
function mapSignal(...params) {
  if (params.length < 2) {
    throw new Error('Invalid param count.  At least two are required.');
  }
  return isFromReactiveParameters(params) ? createFromReactiveParameters(...params) : createFromValue(params[0], params[1], params[2]);
  function isFromReactiveParameters(value) {
    return isReactive(value[0]);
  }
}
/** Creates a readonly signal that selects from one or more signals. */
function createFromReactiveParameters(...params) {
  const {
    inputs,
    options,
    selector
  } = destructureParams();
  return computed(() => selector(...inputs.map(x => x())), options);
  function destructureParams() {
    let options;
    let endOffset;
    if (hasOptions(params)) {
      options = params[params.length - 1];
      endOffset = 2;
    } else {
      options = {};
      endOffset = 1;
    }
    return {
      inputs: params.slice(0, params.length - endOffset).map(x => coerceSignal(x, options)),
      options,
      selector: params[params.length - endOffset]
    };
  }
  function hasOptions(value) {
    // relies on both selector being a function, so if the last element isn't a function then it must be options.
    return typeof value[value.length - 1] !== 'function';
  }
}
/** Creates a new signal that runs selector after every value change. */
function createFromValue(initialValue, selector, options = {}) {
  const $input = signal(initialValue);
  const inputNode = $input[SIGNAL];
  const $output = computed(() => selector($input()), options);
  $output.asReadonly = asReadonlyFnFactory($output);
  $output.input = $input;
  $output.set = value => signalSetFn(inputNode, value);
  $output.update = updateFn => signalUpdateFn(inputNode, updateFn);
  return $output;
}

/**
 * Creates a signal that determines if a document matches a given media query.
 * This uses {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia | window.matchMedia} internally.
 * @param querySource For a writable signal pass a string, otherwise a {@link ReactiveSource} that returns queries as a string.
 * @param options An optional object that affects behavior of the signal.
 * @example
 * ```ts
 * // As a writable signal.
 * const $orientationQry = mediaQuerySignal('(orientation: portrait)');
 * effect(() => console.log(`browser is in ${$orientationQry().matches ? 'portrait' : 'landscape'} orientation`));
 * // from another signal.
 * const $querySource = signal(`(min-width: 992px)`);
 * const $widthQry = mediaQuerySignal($querySource);
 * effect(() => console.log(`browser is in ${$widthQry().matches ? 'large' : 'small'} screen`));
 * ```
 */
function mediaQuerySignal(querySource, options) {
  if (!globalThis.matchMedia) {
    return createDummyOutput('matchMedia is not supported');
  }
  /** The cleanup function for the active MediaQueryList.change eventListener. */
  let cleanupFn = () => {};
  /** When true, no more changes should be observed. */
  let isDestroyed = false;
  return isReactive(querySource) ? createFromReactiveSource(querySource) : createFromValue(querySource);
  /**
   * Creates the output signal from a {@link ReactiveSource}.
   * This is currently done with an effect, but it might be okay if it were done from a computed signal.
   * The only caveat is that there would have to be a side-effect every time the query changed:
   * A new matchMedia query would have to be created, and the event subscribed to.
   * This is probably okay because it should be asynchronous.
   */
  function createFromReactiveSource(queryReactiveSource) {
    const $query = coerceSignal(queryReactiveSource, options);
    let currentQuery = untracked($query);
    const $output = initOutput(currentQuery);
    const outputNode = $output[SIGNAL];
    const effectRef = effect(() => {
      const nextQuery = $query();
      if (currentQuery !== nextQuery) {
        // because we eagerly run matchMedia, the value of query could be the same and therefore there's no point to run setup again.
        currentQuery = nextQuery;
        onQueryChange(outputNode, currentQuery);
      }
    }, {
      ...options,
      manualCleanup: true
    } // cleanup is handled in onQueryChange, destroy and destroyRef.
    );
    $output.destroy = () => {
      effectRef.destroy(); // make sure the effect is destroyed.
      handleDestroy();
    };
    getDestroyRef(mediaQuerySignal, options?.injector).onDestroy($output.destroy);
    return $output;
  }
  function createFromValue(query) {
    const $output = initOutput(query);
    const outputNode = $output[SIGNAL];
    $output.asReadonly = asReadonlyFnFactory($output);
    $output.destroy = handleDestroy;
    $output.set = query => onQueryChange(outputNode, query);
    $output.update = updateFn => onQueryChange(outputNode, updateFn(outputNode.value.media));
    if (!options?.manualDestroy) {
      getDestroyRef(mediaQuerySignal, options?.injector).onDestroy(handleDestroy);
    }
    return $output;
  }
  function handleDestroy() {
    if (!isDestroyed) {
      isDestroyed = true;
      cleanupFn();
    }
  }
  /**
   * Creates the $output signal and begins listening for media query changes.
   */
  function initOutput(initialQuery) {
    const mql = globalThis.matchMedia(initialQuery);
    const $output = createSignal({
      matches: mql.matches,
      media: mql.media
    });
    setDebugNameOnNode($output[SIGNAL], options?.debugName);
    updateQueryListener($output[SIGNAL], mql);
    return $output;
  }
  /** Will close the current listener, and listen to the new query if the signal isn't already destroyed. */
  function onQueryChange(outputNode, query) {
    if (isDestroyed) {
      return;
    }
    const mql = globalThis.matchMedia(query);
    signalSetFn(outputNode, {
      matches: mql.matches,
      media: mql.media
    });
    updateQueryListener(outputNode, mql);
  }
  /** Lists to a queryList change event, and sets global cleanupFn. */
  function updateQueryListener(outputNode, queryList) {
    // ensure old listener is cleaned up.
    cleanupFn();
    queryList.addEventListener('change', handleMediaQueryListEvent);
    cleanupFn = () => queryList.removeEventListener('change', handleMediaQueryListEvent);
    function handleMediaQueryListEvent(event) {
      signalSetFn(outputNode, {
        matches: event.matches,
        media: event.media
      });
    }
  }
}
function createDummyOutput(query) {
  const $output = createSignal({
    matches: false,
    media: query
  });
  $output.asReadonly = asReadonlyFnFactory($output);
  $output.destroy = () => {};
  $output.set = () => {};
  $output.update = () => {};
  return $output;
}

/**
 * Creates a signal whose value may have several, deeply nested signals.
 * Any time any of the nested signals are updated, the signal will be updated as well.
 * The returned value from the signal will be all of the resolved values from the nested signals.
 * @param source The nest object that contains signals.
 * @param options Options that effect the behavior of the signal.
 * @example
 * ```ts
 * const $count = signal(0);
 * const $text = signal('hello');
 * const $why = signal({ count: computed(() => $count() + 1), text: [$text] })
 * const $nested = nestSignal({ count: $count, text: $text, why: $why });
 * console.log($nested()); // [LOG]: { count: 0, text: 'hello', why: { count: 1, text: ['hello'] } }
 * $count.set(1);
 * console.log($nested()); // [LOG]: { count: 1, text: 'hello', why: { count: 2, text: ['hello'] } }
 * ```
 */
function nestSignal(source, options) {
  return isReactive(source) ? createFromReactiveSource(source, options) : createFromValue(source, options);
  function createFromReactiveSource(reactiveSource, options) {
    const $input = coerceSignal(reactiveSource, options);
    return computed(() => deNestRoot($input()), options);
  }
  function createFromValue(initialValue, options) {
    const $source = createSignal(initialValue);
    const sourceNode = $source[SIGNAL];
    const $unwrapped = computed(() => deNestRoot($source()), options);
    $unwrapped.asReadonly = asReadonlyFnFactory($unwrapped);
    $unwrapped.set = value => signalSetFn(sourceNode, value);
    $unwrapped.update = updateFn => signalUpdateFn(sourceNode, updateFn);
    return $unwrapped;
  }
  function deNestRoot(input) {
    /** To prevent infinite loops, the results of denesting objects are cached. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nestCache = new WeakMap();
    const stack = [];
    const deNestFn = options?.ignoreErrors ? deNestSafe : deNestUnsafe;
    return deNestFn(input);
    function deNestUnsafe(input) {
      const stackEntryOutput = stack.find(([stackInput]) => stackInput === input)?.[1];
      if (stackEntryOutput) {
        // to prevent recursion, we need to return the output if the input was found in the stack.
        return stackEntryOutput;
      } else if (isSignal(input)) {
        return getOrSetInCache(input, () => deNestFn(input()));
      } else if (Array.isArray(input)) {
        return getOrSetInCache(input, () => {
          const output = [];
          stack.push([input, output]);
          input.forEach(x => output.push(deNestFn(x)));
          stack.pop();
          return output;
        });
      } else if (input instanceof Set) {
        return getOrSetInCache(input, () => {
          const output = [];
          stack.push([input, output]);
          input.forEach(value => output.push(deNestFn(value)));
          stack.pop();
          return output;
        });
      } else if (input instanceof Map) {
        return getOrSetInCache(input, () => {
          const output = [];
          stack.push([input, output]);
          input.forEach((value, key) => output.push([deNestFn(key), deNestFn(value)]));
          stack.pop();
          return output;
        });
      } else if (input instanceof Date) {
        return input;
      } else if (typeof input === 'object' && input !== null) {
        return getOrSetInCache(input, () => {
          const output = {};
          stack.push([input, output]);
          Object.entries(input).forEach(([key, value]) => output[key] = deNestFn(value));
          stack.pop();
          return output;
        });
      } else {
        return input;
      }
    }
    function deNestSafe(input) {
      try {
        return deNestUnsafe(input);
      } catch (err) {
        if (isDevMode()) {
          console.warn('An error was caught that would be ignored in production.');
          console.error(err);
        }
        return undefined;
      }
    }
    function getOrSetInCache(input, resolver) {
      if (nestCache.has(input)) {
        return nestCache.get(input);
      } else {
        const value = resolver();
        nestCache.set(input, value);
        return value;
      }
    }
  }
}
;
/**
 * Creates a WriteableSignal similar to array.reduce:
 * Every time a value is set, callbackFn is run with the previous value to create a new value.
 * @param initialValue The signal's initial value
 * @param callbackFn The callback run when the signal is set that will create its value
 * @param options The equality function that will run after callbackFn
 * @returns A writable signal whose output value is run through the callbackFn.
 * @example
 * ```ts
 * const accumulateValue = reduceSignal(1, (prior, cur) => prior + cur);
 * console.log(accumulateValue()); // 1
 * accumulateValue.set(5);
 * console.log(accumulateValue()); // 6
 * accumulateValue.update(x => x + 1);
 * console.log(accumulateValue()); // 13
 * ```
 */
function reduceSignal(initialValue, callbackFn, options = {}) {
  const $output = createSignal(initialValue);
  const outputNode = $output[SIGNAL];
  setDebugNameOnNode(outputNode, options.debugName);
  setEqualOnNode(outputNode, options.equal);
  $output.asReadonly = asReadonlyFnFactory($output);
  $output.set = value => signalUpdateFn(outputNode, prior => callbackFn(prior, value));
  $output.update = itemUpdateFn => signalUpdateFn(outputNode, prior => callbackFn(prior, itemUpdateFn(prior)));
  return $output;
}

/** A cursor for ArrayLike objects. */
class ArrayLikeCursor {
  constructor(source, autoReset) {
    this.source = source;
    this.autoReset = autoReset;
    this.index = -1;
  }
  /**
   * Performs a forward search from current position.
   * Search entire collection if autoReset is true.
   * Will set position past end if nothing is found.
   *
   * @returns The current cursor result.
   */
  moveTo(value) {
    // search from position first.
    for (let i = this.index + 1; i < this.source.length; i++) {
      if (this.source[i] === value) {
        this.index = i;
        return {
          hasValue: true,
          value
        };
      }
    }
    // if not found search from start.
    if (this.autoReset) {
      for (let i = 0; i <= this.index; i++) {
        if (this.source[i] === value) {
          this.index = i;
          return {
            hasValue: true,
            value
          };
        }
      }
    }
    this.index = this.source.length; // set to end to indicate search was unsuccessful.
    return {
      hasValue: false
    };
  }
  next(relativeChange = 1) {
    if (this.source.length === 0) {
      return {
        hasValue: false
      };
    }
    if (relativeChange === 0 && this.index === -1) {
      // auto move forward if relativeChange is 0, and we haven't moved forward yet.
      return {
        hasValue: true,
        value: this.source[this.index = 0]
      };
    }
    if (this.autoReset) {
      if (this.index === -1 && relativeChange < 0) {
        this.index = 0; // it doesn't make much sense to go backwards from -1 so assume it was at the start element.
      }
      this.index = ((this.index + relativeChange) % this.source.length + this.source.length) % this.source.length;
    } else {
      // stay constrained to within the array bounds + 1.
      this.index = Math.max(-1, Math.min(this.index + relativeChange, this.source.length));
    }
    return {
      hasValue: this.index > -1 && this.index < this.source.length,
      value: this.source[this.index]
    };
  }
  reset() {
    this.index = -1;
  }
}
const ERR_BACKWARD_MOVE = 'Backwards movement is not supported.';
const ERR_UNABLE_TO_RESET = 'The underlying iterable cannot be reset.  This could be because the iterable is a generator.';
class IterableCursor {
  /**
   *
   * @param source The iterable to iterate.
   * @param autoReset If true, when the iterator is a the end, the cursor will create a new iterator and start from the beginning.
   * @param maxSearchSize The maximum number of iterations to search before giving up.
   */
  constructor(source, autoReset, maxSearchSize = 65536) {
    this.source = source;
    this.autoReset = autoReset;
    this.maxSearchSize = maxSearchSize;
    this.currentItem = {
      hasValue: false
    };
    this.iterator = source[Symbol.iterator]();
  }
  moveTo(value) {
    // search from position first, returning the result if it is found or autoReset is off.
    this.searchFromCurrentPosition(value);
    if (this.currentItem.hasValue || !this.autoReset) {
      return this.currentItem;
    }
    this.reset();
    this.searchFromCurrentPosition(value);
    return this.currentItem;
  }
  next(relativeChange = 1) {
    if (relativeChange < 0) {
      throw new Error(ERR_BACKWARD_MOVE);
    }
    if (relativeChange === 0) {
      if (!this.currentItem.hasValue) {
        // try to see if a next value can be retrieved.
        const res = this.iterator.next();
        if (!res.done) {
          this.currentItem = {
            hasValue: true,
            value: res.value
          };
        }
      }
    } else {
      for (let i = 0; i < relativeChange; i++) {
        let res = this.iterator.next();
        if (!res.done) {
          this.currentItem = {
            hasValue: true,
            value: res.value
          };
        } else if (this.autoReset) {
          this.reset();
          res = this.iterator.next();
          if (res.done) {
            this.currentItem = {
              hasValue: false
            };
            break;
          }
          this.currentItem = {
            hasValue: true,
            value: res.value
          };
        } else {
          this.currentItem = {
            hasValue: false
          };
          break;
        }
      }
    }
    return {
      ...this.currentItem
    };
  }
  reset() {
    const nextCursor = this.source[Symbol.iterator]();
    if (nextCursor === this.iterator) {
      throw new Error(ERR_UNABLE_TO_RESET);
    }
    this.iterator = nextCursor;
    this.currentItem = {
      hasValue: false
    };
  }
  /** Used in {@link moveTo} to search from the current position. */
  searchFromCurrentPosition(value) {
    let elementsSearched = 0;
    let result = this.iterator.next();
    while (!result.done) {
      if (result.value === value) {
        this.currentItem = {
          hasValue: true,
          value
        };
        return;
      }
      if (++elementsSearched >= this.maxSearchSize) {
        break;
      }
      result = this.iterator.next();
    }
    this.currentItem = {
      hasValue: false
    };
  }
}
const ERR_BOUNDS_EXCEEDED = 'Sequence cursor has exceeded bounds.';
const ERR_ELEMENT_NOT_PRESENT = 'Element was not present in sequence.';
const ERR_NO_ELEMENTS = 'Sequence contains no elements.';
const ERR_INVALID_SOURCE = 'Invalid source type passed to sequence signal.';
/**
 * This creates a signal that will cycle through a source of values, returning the the start after each call of next.
 * @param sequence The source of sequence values.
 * Could be an ArrayLike object or a "Cursor" which is like an iterator, but with a reset method.
 * @param options Options for creating the sequence signal.
 * @returns The sequence signal.
 * @example
 * ```ts
 * const boolSeq = sequenceSignal([true, false]);
 * console.log(boolSeq()) // true
 * boolSeq.next();
 * console.log(boolSeq()) // false
 * boolSeq.next();
 * console.log(boolSeq()) // true
 *
 * const lettersSeq = sequenceSignal('ABCDEFG');
 * console.log(letterSeq()) // A;
 * boolSeq.next(2);
 * console.log(letterSeq()) // C;
 *
 * // pass a "Cursor" to generate a sequence.
 * sequenceSignal((() => {
 *   let values = [1, 2];
 *   return {
 *     next: (relativeChange: number) => {
 *       for (let i = 0; i < relativeChange; i++) {
 *         values = [values[1], values[0] + values[1]];
 *       }
 *       for (let i = relativeChange; i < 0; i++) {
 *         values = [Math.max(1, values[1] - values[0]), Math.max(values[0], 2)];
 *       }
 *       return { hasValue: true, value: values[0] };
 *     },
 *     reset: () => values = [1, 2]
 *   };
 * })());
 * console.log(fibSeq()); // 1;
 * fibSeq.next();
 * console.log(fibSeq()); // 2;
 * ```
 */
function sequenceSignal(sequence, options = {}) {
  const sequenceCursorGetter = isReactive(sequence) ? createCursorGetterFromReactiveSource(sequence) : createCursorGetterFromValue(sequence);
  const $output = createSignal(getFirstValue(sequenceCursorGetter()));
  const outputNode = $output[SIGNAL];
  setDebugNameOnNode(outputNode, options.debugName);
  $output.next = (relativeChange = 1) => {
    const res = sequenceCursorGetter().next(relativeChange);
    if (!res.hasValue) {
      throw new Error(ERR_BOUNDS_EXCEEDED);
    }
    signalSetFn(outputNode, res.value);
  };
  $output.reset = () => {
    sequenceCursorGetter().reset();
    $output.next();
  };
  $output.set = value => {
    const res = sequenceCursorGetter().moveTo(value);
    if (!res.hasValue) {
      throw new Error(ERR_ELEMENT_NOT_PRESENT);
    }
    signalSetFn(outputNode, value);
  };
  $output.update = updateFn => {
    const nextValue = updateFn(outputNode.value);
    $output.set(nextValue);
  };
  return $output;
  /**
   * Creates function that gets the current cursor from a {@link ReactiveSource}.
   * This is done in lieu of using an effect.
   */
  function createCursorGetterFromReactiveSource(inputSource) {
    const $input = coerceSignal(inputSource, options);
    let lastSequence = untracked($input);
    let cachedCursor = sequenceSourceToCursor(lastSequence);
    return () => {
      const currentSequence = untracked($input);
      if (currentSequence !== lastSequence) {
        lastSequence = currentSequence;
        cachedCursor = sequenceSourceToCursor(lastSequence);
      }
      return cachedCursor;
    };
  }
  /** Creates function that gets a cached cursor from a value.  */
  function createCursorGetterFromValue(value) {
    const cursor = sequenceSourceToCursor(value);
    return () => cursor;
  }
  /** Ensure that there's a first value. */
  function getFirstValue(cursor) {
    const res = cursor.next();
    if (!res.hasValue) {
      throw new Error(ERR_NO_ELEMENTS);
    }
    return res.value;
  }
  /** This should be good enough to narrow down array like objects such as strings. */
  function isArrayLike(value) {
    return typeof value['length'] === 'number';
  }
  /** Determines if an object is compatible with a {@link Cursor} by checking for next and rest properties. */
  function isCursor(value) {
    return typeof value === 'object' && 'next' in value && 'reset' in value;
  }
  /** Converts a {@link SequenceSource} to a {@link Cursor}.  If for some reason it can't be done, throws an error. */
  function sequenceSourceToCursor(data) {
    if (isCursor(data)) {
      return data;
    }
    const cursor = isArrayLike(data) ? new ArrayLikeCursor(data, !options.disableAutoReset) : Symbol.iterator in data ? new IterableCursor(data, !options.disableAutoReset) : undefined;
    if (!cursor) {
      throw new Error(ERR_INVALID_SOURCE);
    }
    return cursor;
  }
}

/** A functioning implementation of browser Storage that can be used in tests and is backed by Map */
class MapBasedStorage {
  constructor() {
    this.storageMap = new Map();
  }
  get length() {
    return this.storageMap.size;
  }
  clear() {
    return this.storageMap.clear();
  }
  getItem(key) {
    return this.storageMap.get(key) ?? null;
  }
  key(index) {
    return Array.from(this.storageMap.keys())[index] ?? null;
  }
  removeItem(key) {
    this.storageMap.delete(key);
  }
  setItem(key, value) {
    this.storageMap.set(key, value);
  }
}

/** Combines web storage with JSON serialization in a signature compatible with Map. */
class WebObjectStore {
  /**
   * Initializes BrowserStorageStore.
   *
   * @param webStorage Provider of web storage.
   * @param replacer An optional replacer function when serializing JSON.
   * @param reviver An optional reviver function when deserializing JSON.
   */
  constructor(webStorage, replacer, reviver) {
    this.webStorage = webStorage;
    this.replacer = replacer;
    this.reviver = reviver;
  }
  /** Gets an item or returns undefined if it does not exist. */
  get(key) {
    const valueRaw = this.webStorage.getItem(key);
    return valueRaw === null ? undefined : JSON.parse(valueRaw, this.reviver);
  }
  /**
   * Sets an item in storage with the given key.
   * If undefined is passed, then the item is removed since that is not valid json and cannot be serialized.
   */
  set(key, value) {
    if (value === undefined) {
      this.webStorage.removeItem(key);
    } else {
      this.webStorage.setItem(key, JSON.stringify(value, this.replacer));
    }
  }
}

/**
 * Creates a signal that will sync changes to some sort of storage.
 * The next time a signal with the same key is read, an alternative value to the initial value will be used.
 * It's probably better to use {@link sessionStorageSignal} or {@link localStorageSignal} instead of this.
 * @param initialValue The initialValue for the signal if it isn't in storage.
 * @param key The key to use for storage.  This should be unique to avoid conflicts when deserializing values.
 * @param storageProvider The provider of storage.
 * @param options Standard create signal options.
 * @typeParam T The type of the value that should be stored and deserialized.
 * @returns A writable signal
 * @example
 * ```ts
 * const storageProvider = Map<string, number>();
 * const signal1 = storageSignal(1, 'someKey', storageProvider);
 * signal1.set(100);
 * const signal2 = storageSignal(1, 'someKey', storageProvider);
 * console.log(signal1(), signal2()); // [LOG]: 100, 100
 * ```
 */
function storageSignal(initialValue, key, storageProvider, options = {}) {
  const storageValue = storageProvider.get(key);
  const $output = createSignal(storageValue === undefined ? initialValue : storageValue);
  const outputNode = $output[SIGNAL];
  // The equal function needs to be checked BEFORE setting storage or the value will be inconsistent.
  const equalFn = options.equal ?? outputNode.equal;
  setDebugNameOnNode(outputNode, options.debugName);
  $output.asReadonly = () => $output;
  $output.set = value => {
    if (!equalFn(value, outputNode.value)) {
      storageProvider.set(key, value);
      signalSetFn(outputNode, value);
    }
  };
  $output.update = updateFn => {
    const next = updateFn(outputNode.value);
    if (!equalFn(next, outputNode.value)) {
      storageProvider.set(key, next);
      signalSetFn(outputNode, next);
    }
  };
  return $output;
}
/** Fallback storage provider for localStorageSignal.  Not assigned until needed. */
let LOCAL_STORAGE_FALLBACK;
/** Fallback storage provider for sessionStorageSignal.  Not assigned until needed. */
let SESSION_STORAGE_FALLBACK;
/**
 * Generates a signal using localStorage as the store.  A shared Map is used if session storage is not supported.
 *
 * @param initialValue the initial value for the signal
 * @param key the key to use in localStorage
 * @param options optional options to configure the signal and underlying storage.
 * @typeParam T The type of the value that should be stored and deserialized.
 * @returns the writable signal generated from storageSignal.
 * @example
 * ```ts
 * const signal1 = localStorageSignal(1, 'someKey');
 * console.log(signal1()); // This MIGHT not be 1 depending on what was stored in localStorage for "someKey".
 * signal1.set(100);
 * console.log(signal1()); // 100 ("someKey" is now 100 in localStorage)
 * ```
 * @see {@link storageSignal}
 */
function localStorageSignal(initialValue, key, options = {}) {
  const storage = globalThis.localStorage ?? (LOCAL_STORAGE_FALLBACK ??= new MapBasedStorage());
  const store = new WebObjectStore(storage, options.replacer, options.reviver);
  return storageSignal(initialValue, key, store, options);
}
/**
 * Generates a signal using sessionStorage as the store.  A shared Map is used if session storage is not supported.
 *
 * @param initialValue the initial value for the signal
 * @param key the key to use in sessionStorage
 * @param options optional options to configure the signal and underlying storage.
 * @typeParam T The type of the value that should be stored and deserialized.
 * @returns the writable signal generated from storageSignal.
 * @example
 * ```ts
 * const signal1 = sessionStorageSignal(1, 'someKey');
 * console.log(signal1()); // This MIGHT not be 1 depending on what was stored in sessionStorage for "someKey".
 * signal1.set(100);
 * console.log(signal1()); // 100 ("someKey" is now 100 in sessionStorage)
 * ```
 * @see {@link storageSignal}
 */
function sessionStorageSignal(initialValue, key, options = {}) {
  const storage = globalThis.sessionStorage ?? (SESSION_STORAGE_FALLBACK ??= new MapBasedStorage());
  const store = new WebObjectStore(storage, options.replacer, options.reviver);
  return storageSignal(initialValue, key, store, options);
}

/**
 * Creates a signal that acts either a timer or interval.
 * Emitting increasing numbers for each iteration, starting with an initial 0.
 * Using a {@link ReactiveSource} as a parameter will cause the timer to immediately emit if it is reduced to an amount that would
 * move due time into the past.
 * @param timerTime A constant or {@link ReactiveSource} that emits how long until the timer is due.
 * @param intervalTime An optional constant or {@link ReactiveSource} that emits how long until the timer is due after the initial time was emitted.
 * @param options An optional object that affects behavior of the signal.
 * @example
 * ```ts
 * const dueInASecond = timerSignal(1000);
 * const dueEverySecond = timerSignal(1000, 1000);
 * const dueTime = computed(() => 500 + dueEverySecond() * 50);
 * const adjustableDueTime = timerSignal(dueTime, dueTime);
 *
 * effect(() => console.log('due in a second', dueInASecond()));
 * effect(() => console.log('due every second', dueEverySecond()));
 * effect(() => console.log('due every second', adjustableDueTime()));
 * ```
 */
function timerSignal(timerTime, intervalTime, options) {
  const injector = options?.injector ?? getInjector(timerSignal);
  const timerTimeFn = createGetValueFn(timerTime, injector);
  const intervalTimeFn = intervalTime != null ? createGetValueFn(intervalTime, injector) : undefined;
  /** The signal that will be returned. */
  const $output = signal(0, options);
  /** Keeps track of the state of the timer. */
  const $state = signal('stopped');
  const timer = new TimerInternal(timerTimeFn(), intervalTimeFn?.(), {
    onStatusChange: internalStatus => $state.set(transformTimerStatus(internalStatus)),
    onTick: x => $output.set(x),
    runAtStart: !options?.stopped && isPlatformBrowser(injector.get(PLATFORM_ID))
  });
  // setup cleanup actions.
  getDestroyRef(timerSignal, options?.injector).onDestroy(() => timer.destroy());
  // watch for changes to update timer properties.
  watchValueSourceFn(timerTimeFn, x => timer.timeoutTime = x, injector);
  watchValueSourceFn(intervalTimeFn, x => timer.intervalTime = x, injector);
  // bind timer functions to output.
  return createTimerSignal($output, timer);
  /** Assigns timer functions to the signal. */
  function createTimerSignal(outputSignalFn, timer) {
    outputSignalFn.pause = timer.pause.bind(timer);
    outputSignalFn.restart = () => {
      outputSignalFn.set(0);
      timer.start();
    };
    outputSignalFn.resume = timer.resume.bind(timer);
    outputSignalFn.state = $state;
    return outputSignalFn;
  }
  function transformTimerStatus(status) {
    switch (status) {
      case TimerStatus.Destroyed:
        return 'destroyed';
      case TimerStatus.Running:
        return 'running';
      case TimerStatus.Paused:
        return 'paused';
      case TimerStatus.Stopped:
        return 'stopped';
    }
  }
}

/**
 * An effect that will start, stop, or conditionally run based on conditions set in the {@link GatedEffectOptions}.
 * This is most effective for stopping an effect as it will properly clean up after itself and not track signals unnecessarily.
 *
 * @param effectFn The function to be executed whenever all gate conditions are met.
 * @param options Standard effect options, plus gate conditions filter (condition that prevents running),
 * start (start running effect), times (number of times to execute), and until (stop running effect)
 * @example
 * ```ts
 * const $userInfo = signal<UserInfo | undefined>(undefined);
 * gatedEffect(() => doSomethingWithRequiredUserInfo($userInfo()!), { start: () => $userInfo() !== undefined, times: 1 });
 * ```
 */
function gatedEffect(effectFn, options = {}) {
  /** Tracks the amount of times run, only valid if the {@link GatedEffectOptions.times} is set. */
  let timesRun = 0;
  // These are destructured because changes in the object could cause side effects.
  const {
    filter: filterOpt,
    start: startOpt,
    times: timesOpt,
    until: untilOpt
  } = options;
  /** True when the {@link GatedEffectOptions.start} condition is met.  This is initially true if the {@link GatedEffectOptions.start} condition is undefined. */
  let startConditionMet = startOpt === undefined;
  const hasFilter = filterOpt !== undefined;
  const hasUntil = untilOpt !== undefined;
  const effectRef = effect(onCleanup => {
    if (!startConditionMet) {
      // if the condition is not met, do not run the effect.
      if (!startOpt()) {
        return;
      }
      startConditionMet = true;
    }
    if (hasFilter && !filterOpt()) {
      // prevent from running if the filter is not met.
      return;
    }
    if (timesOpt !== undefined) {
      // if times run condition is met, then destroy the effect and return.
      if (timesRun >= timesOpt) {
        effectRef.destroy();
        return;
      }
      timesRun += 1;
    }
    if (hasUntil && untracked(() => untilOpt())) {
      // if until condition is met, then destroy the effect and return.
      // there is no point in untilOpt to be tracked - any signals inside of the effectFn will trigger the effect and until must run before it.
      effectRef.destroy();
      return;
    }
    effectFn(onCleanup);
  }, options);
  return effectRef;
}

/**
 * Creates an async iterator for a signal.
 * @param source The signal to create an async iterator for.
 * @param options Options for the signal.
 * @example
 * ```ts
 * // assumes this is within a component
 * readonly source = signal('initial');
 * constructor() {
 *   for await (const item of signalToIterator(this.source)) {
 *     console.log(item); // 'initial', 'next';
 *   }
 *   this.source.set('next');
 * }
 * ```
 */
function signalToIterator(source, options) {
  /** The value of done that can be changed by complete. */
  let doneValue = undefined;
  /** Stores the first value which is necessary only for preventing double initial emissions. */
  const initialValue = source();
  /** The injector in which the effect in which this function is part of. */
  const injector = options?.injector ?? getInjector(signalToIterator);
  /** Necessary in case there are any additional calls to the returned iterator's .next() but watcher has been destroyed. */
  let isDone = false;
  /** Necessary to check if the first value had been emitted already. */
  let isFirstEffectComplete = false;
  /** Promises that have been created that are waiting to be resolved or rejected because next has been called. */
  const unresolvedQueue = [];
  /** Values that have already been retrieved before next has been called. */
  const unyieldedQueue = [];
  // add initial result;
  addResult({
    done: false,
    value: initialValue
  });
  const watcher = effect(() => {
    const value = source();
    if (isFirstEffectComplete) {
      addResult({
        done: false,
        value
      });
    } else {
      // to ensure the first element is emitted it was immediately added.
      // The issue is that we have to skip the first effect emission.
      // But the value could have changed!
      // This could have a bug with equality if the intention was to emit a value that was the same.
      isFirstEffectComplete = true;
      if (value !== initialValue) {
        addResult({
          done: false,
          value
        });
      }
    }
  }, {
    injector,
    manualCleanup: true
  });
  injector.get(DestroyRef).onDestroy(() => complete(([resolve]) => resolve({
    done: true,
    value: doneValue
  })));
  return {
    [Symbol.asyncIterator]() {
      return this;
    },
    next() {
      if (unyieldedQueue.length > 0) {
        return Promise.resolve(unyieldedQueue.shift());
      } else if (isDone) {
        return Promise.resolve({
          done: true,
          value: doneValue
        });
      }
      return new Promise((resolve, reject) => unresolvedQueue.push([resolve, reject]));
    },
    return: value => {
      doneValue = value;
      complete(([resolve]) => resolve({
        done: true,
        value
      })); // resolve outstanding promises
      return Promise.resolve({
        done: true,
        value
      });
    },
    throw: err => {
      complete(([, reject]) => reject(err)); // reject outstanding promises
      return Promise.reject(err);
    }
  };
  /** Either resolves a waiting promise or adds a result to resolve later. */
  function addResult(result) {
    if (unresolvedQueue.length > 0) {
      const [resolve] = unresolvedQueue.shift();
      resolve(result);
    } else {
      unyieldedQueue.push(result);
    }
  }
  ;
  /** Runs cleanup when either the return method is called or injection context is destroyed */
  function complete(unresolvedAction) {
    // we need to have this check since this can be completed by more than one event.
    if (!isDone) {
      watcher.destroy();
      isDone = true;
      if (unresolvedQueue.length > 0) {
        flushQueue(unresolvedQueue, unresolvedAction);
      } else {
        addResult({
          done: true,
          value: doneValue
        }); // make sure a done result is returned.
      }
    }
  }
  /** Removes all elements of the queue calling callback after each element is removed */
  function flushQueue(queue, callback) {
    while (queue.length > 0) {
      callback(queue.shift());
    }
  }
}

/**
 * Given that {@link inspect} should have no side-effects and does nothing in production mode,
 * this is provided as a simple way to set the default behavior without having to get into dependency injection.
 * You can modify this from wherever you want.
 */
const INSPECT_DEFAULTS = {
  /** This is the default reporter for inspect. */
  reporter: value => console.log(value),
  /** If true, this will run even if in prod mode is enabled. */
  runInProdMode: false,
  /** If true, the initial value will not be reported. */
  skipInitial: false
};
/**
 * Reports the latest state of a subject by resolving all the values deeply contained within by using {@link nestSignal}.
 * By default the output is just a console.log, but this can be changed with the {@link InspectOptions<T>.reporter} option.
 * Subject can be anything, but to be effective it should be a signal, an object that contains signals, or an array of signals.
 *
 * @param subject This can be anything, but ideally it is some object that contains signals somewhere.
 * @param options Options that control the behavior of inspect.  Globally, options can be changed from {@link INSPECT_DEFAULTS}.
 * @example
 * ```ts
 * const $a = signal(1);
 * const $b = signal(2);
 * const $c = signal({ a: $a, b: $b });
 * inspect([$c]); // log: [{ a: 1, b: 2 }];
 * $b.set(3) // log: [{ a: 1, b: 3 }];
 * ```
*/
function inspect(subject, options) {
  if (!isDevMode() && !(options?.runInProdMode ?? INSPECT_DEFAULTS.runInProdMode)) {
    return;
  }
  let reporter = options?.reporter ?? INSPECT_DEFAULTS.reporter;
  if (options?.skipInitial ?? INSPECT_DEFAULTS.skipInitial) {
    const nextReporter = reporter;
    reporter = () => reporter = nextReporter;
  }
  const $input = nestSignal(subject, {
    ...options,
    ignoreErrors: true
  });
  effect(() => reporter($input()), {
    ...options
  });
}

/**
 * Angular Signal Generators Main Library
 * @module @ddtmm/angular-signal-generators
 */
// all signals and utilities.

/**
 * Generated bundle index. Do not edit.
 */

export { ERR_BOUNDS_EXCEEDED, ERR_ELEMENT_NOT_PRESENT, ERR_INVALID_SOURCE, ERR_NO_ELEMENTS, INSPECT_DEFAULTS, asyncSignal, createInterpolator, debounceSignal, eventSignal, filterSignal, gatedEffect, inspect, intersectionSignal, liftSignal, localStorageSignal, mapSignal, mediaQuerySignal, mutationSignal, nestSignal, reduceSignal, resizeSignal, sequenceSignal, sessionStorageSignal, signalToIterator, springSignal, storageSignal, timerSignal, tweenSignal };
